/** Injected from `index-template.html` as `HandyApiBinKey` when present. */
declare var HandyApiBinKey: string | undefined;

const HANDY_API_DEFAULT_KEY = "PUB-0YA974790j2USG3nQhYuk";
const HANDY_BIN_BASE = "https://data.handyapi.com/bin/";
/** HandyAPI expects the leading digits in the path (8-digit BIN / range prefix). */
const HANDY_BIN_DIGITS = 8;

/** Matches server `CardFundingType` enum names for JSON `cardFundingType`. */
export type BackendCardFundingType = "Credit" | "Debit" | "Prepaid";

function handyApiKey(): string {
    const fromPage = typeof HandyApiBinKey === "string" ? HandyApiBinKey.trim() : "";
    return fromPage || HANDY_API_DEFAULT_KEY;
}

export function extractCardBin8(cardNumber: string): string | null {
    const digits = String(cardNumber || "").replace(/\D/g, "");
    if (digits.length < HANDY_BIN_DIGITS) return null;
    return digits.slice(0, HANDY_BIN_DIGITS);
}

function mapHandyTypeToFunding(typeRaw: string | undefined): BackendCardFundingType | undefined {
    if (!typeRaw) return undefined;
    const u = String(typeRaw).replace(/[\s_]/g, "").toUpperCase();
    if (u === "CREDIT") return "Credit";
    if (u === "DEBIT") return "Debit";
    if (u === "PREPAID") return "Prepaid";
    return undefined;
}

type HandyBinResponse = {
    Status?: string;
    Type?: string;
};

/**
 * Looks up BIN at HandyAPI (first 8 digits in the URL path).
 * Returns undefined on network/error/non-success — callers should omit `cardFundingType` and let the server fall back.
 */
export async function fetchCardFundingTypeFromBin8(bin8: string): Promise<BackendCardFundingType | undefined> {
    const bin = String(bin8 || "").replace(/\D/g, "");
    if (bin.length !== HANDY_BIN_DIGITS) return undefined;

    const url = `${HANDY_BIN_BASE}${encodeURIComponent(bin)}`;
    try {
        const res = await fetch(url, {
            method: "GET",
            headers: { "x-api-key": handyApiKey() },
            credentials: "omit",
        });
        if (!res.ok) return undefined;
        const data = (await res.json()) as HandyBinResponse;
        if (String(data?.Status || "").toUpperCase() !== "SUCCESS") return undefined;
        return mapHandyTypeToFunding(data?.Type);
    } catch {
        return undefined;
    }
}

/** Best-effort; never throws. */
export async function resolveCardFundingTypeFromCardNumber(cardNumber: string): Promise<BackendCardFundingType | undefined> {
    const bin8 = extractCardBin8(cardNumber);
    if (!bin8) return undefined;
    return fetchCardFundingTypeFromBin8(bin8);
}
